import axios from 'axios';
import { attachCsrf } from './csrf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Auth lives in HttpOnly cookies (`access_token`, `pro_access_token`) — never in
// localStorage. Legacy copies of a token left by older builds are purged here.
try {
  const legacy = JSON.parse(localStorage.getItem('user') || 'null');
  if (legacy && (legacy.token || legacy.access_token)) {
    const { token: _t, access_token: _a, ...safe } = legacy;
    localStorage.setItem('user', JSON.stringify(safe));
  }
  localStorage.removeItem('pro_token');
  localStorage.removeItem('token');
  // Trusted-device token now lives in an HttpOnly cookie.
  localStorage.removeItem('device_token');
  // Keys accidentally created by an old navbar monkey-patch.
  localStorage.removeItem('setItem');
  localStorage.removeItem('removeItem');
} catch { /* storage unavailable */ }

// Every axios call (shared clients and raw axios) sends the auth cookies.
axios.defaults.withCredentials = true;

// Persist only public user metadata (never a token).
export const saveUser = (user) => {
  const { token: _t, access_token: _a, ...safe } = user || {};
  localStorage.setItem('user', JSON.stringify(safe));
};

const getAuthToken = () => null;

// Create axios instance with auth header
// Security audit (2026-06-28) M-3 — withCredentials sends the HttpOnly
// `access_token` cookie automatically. The legacy `Authorization` header
// is still attached when a token happens to be in localStorage (older
// sessions), so upgrade is transparent for logged-in users.
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});
attachCsrf(apiClient);

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global 402 (insufficient AI credits) handler — dispatches a window event
// so a global Upsell dialog can react without each component coding it.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const code = (detail && typeof detail === 'object') ? detail.code : null;
      const detailStr = typeof detail === 'string' ? detail : '';
      // Trigger upsell on structured 402 OR generic 402 mentioning AI credits / quota épuisé
      const triggerUpsell = status === 402 && (
        code === 'insufficient_credits' ||
        detailStr.includes('Crédits IA') ||
        detailStr.includes('Quota gratuit')
      );
      if (triggerUpsell) {
        window.dispatchEvent(new CustomEvent('realty237:ai-credits-required', {
          detail: typeof detail === 'object'
            ? { ...detail, requestUrl: error.config?.url || '' }
            : { code: 'insufficient_credits', message: detailStr, requestUrl: error.config?.url || '' },
        }));
      }
    } catch (_) { /* noop */ }
    return Promise.reject(error);
  }
);

// =============================================================
// S9 — Silent JWT refresh.
// When a request hits 401 (token expired) we attempt a single
// /auth/refresh call, replace the cached token, and replay the
// original request once. Concurrent 401s are coalesced behind a
// shared in-flight promise so we never trigger multiple refreshes.
// =============================================================
let _refreshInflight = null;

const _refreshAccessToken = async () => {
  // Security audit M-3 — refresh works via the HttpOnly cookie automatically
  // when `withCredentials:true` is set. If a legacy localStorage token still
  // exists we send it as a Bearer header for a smooth transition. On success
  // the server updates the cookie AND returns a new access_token in the body
  // (we intentionally do NOT persist it back to localStorage anymore).
  const { data } = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
  return data.access_token;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const detailStr = typeof detail === 'string' ? detail : (detail?.message || '');

    // Only retry on 401 that looks like an expired token. We skip:
    //   - the refresh endpoint itself (else infinite loop),
    //   - logout / login / register endpoints (no token to refresh),
    //   - requests already retried once (`_retried` flag),
    //   - 401s that are about CAPTCHA / locked accounts (different cause).
    const url = original.url || '';
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');

    const looksLikeExpired =
      status === 401 &&
      !original._retried &&
      !isAuthEndpoint &&
      // exclude business-logic 401s (e.g. captcha_required, account_locked)
      !['captcha_required', 'account_locked', 'invalid_credentials'].includes(
        detail?.code,
      );

    if (!looksLikeExpired) {
      return Promise.reject(error);
    }

    try {
      original._retried = true;
      if (!_refreshInflight) {
        _refreshInflight = _refreshAccessToken().finally(() => {
          _refreshInflight = null;
        });
      }
      const newToken = await _refreshInflight;
      original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
      return apiClient.request(original);
    } catch (refreshErr) {
      // Refresh failed — clear session and force re-login.
      try { localStorage.removeItem('user'); } catch (_) { /* noop */ }
      // Soft redirect so the user lands on the auth page with the next
      // route preserved as ?from=...
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/auth?from=${next}`);
      }
      return Promise.reject(refreshErr);
    }
  },
);

// Auth APIs
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  // Security audit M-3 — clears the HttpOnly access_token cookie server-side.
  logout: () => apiClient.post('/auth/logout'),
  getMe: () => apiClient.get('/auth/me'),
  // S1 — Email verification flow
  verifyEmail: (token) => apiClient.post('/auth/verify-email', { token }),
  resendVerification: () => apiClient.post('/auth/resend-verification'),
  getVerificationStatus: () => apiClient.get('/auth/me/verification-status'),
  // S4 — Password reset flow
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) =>
    apiClient.post('/auth/reset-password', { token, new_password }),
};

// User profile APIs (self-service)
export const userAPI = {
  getMe: () => apiClient.get('/users/me'),
  updateMe: (data) => apiClient.put('/users/me', data),
};

// Agent digital card APIs
export const agentCardAPI = {
  getMySlug: () => apiClient.get('/agents/me/card-slug'),
  generateSlug: () => apiClient.post('/agents/me/card-slug'),
  getCard: (slug) => apiClient.get(`/agents/card/${slug}`),
};

// AI Credits — wallet, packs, subscriptions
export const aiCreditsAPI = {
  me: () => apiClient.get('/ai-credits/me'),
  packs: () => apiClient.get('/ai-credits/packs'),
  startSubscription: (data) => apiClient.post('/ai-credits/subscriptions/start', data),
  listSubscriptions: () => apiClient.get('/ai-credits/subscriptions/me'),
  cancelSubscription: (id) => apiClient.post(`/ai-credits/subscriptions/${id}/cancel`),
  // Admin CMS
  adminGetConfig: () => apiClient.get('/admin/ai-credits-cms'),
  adminUpdateActionCosts: (costs) => apiClient.put('/admin/ai-credits-cms/action-costs', { costs }),
  adminUpdatePacks: (packs) => apiClient.put('/admin/ai-credits-cms/packs', { packs }),
  adminUpdateSubscriptions: (subscriptions) => apiClient.put('/admin/ai-credits-cms/subscriptions', { subscriptions }),
  adminUpdatePromo: (payload) => apiClient.put('/admin/ai-credits-cms/promo', payload),
  adminResetConfig: () => apiClient.post('/admin/ai-credits-cms/reset'),
  adminAudit: (limit = 30) => apiClient.get('/admin/ai-credits-cms/audit', { params: { limit } }),
  adminRollback: (auditId) => apiClient.post(`/admin/ai-credits-cms/audit/${auditId}/rollback`),
};

// Merchant code (manual MTN/Orange payment with admin validation)
export const merchantPaymentsAPI = {
  getCodes: (category = 'default') => apiClient.get(`/payments/merchant-codes/${category}`),
  submit: (data) => apiClient.post('/payments/merchant-code/submit', data),
  mySubmissions: () => apiClient.get('/payments/merchant-code/my-submissions'),
  // Admin
  adminGetConfig: () => apiClient.get('/admin/merchant-codes'),
  adminUpdateConfig: (data) => apiClient.put('/admin/merchant-codes', data),
  adminList: (params) => apiClient.get('/admin/merchant-payments', { params }),
  adminPendingCount: () => apiClient.get('/admin/merchant-payments/pending-count'),
  adminApprove: (id, data) => apiClient.post(`/admin/merchant-payments/${id}/approve`, data || {}),
  adminReject: (id, data) => apiClient.post(`/admin/merchant-payments/${id}/reject`, data),
};

// Property APIs
export const propertyAPI = {
  getAll: (params) => apiClient.get('/properties', { params }),
  list: (params) => apiClient.get('/properties', { params }), // alias
  getFeatured: () => apiClient.get('/properties/featured'),
  getRecommendations: (params) => apiClient.get('/properties/recommendations', { params }),
  getMap: (params) => apiClient.get('/properties/map', { params }),
  getFacets: (params) => apiClient.get('/properties/facets', { params }),
  getById: (id) => apiClient.get(`/properties/${id}`),
  create: (data, options = {}) => {
    // Backward-compat: support old `create(data, planId)` signature when 2nd arg is a string
    const opts = typeof options === 'string' ? { plan_id: options } : options;
    const { plan_id = 'basic', as_draft = false } = opts;
    return apiClient.post('/properties', data, { params: { plan_id, as_draft } });
  },
  update: (id, data) => apiClient.put(`/properties/${id}`, data),
  delete: (id) => apiClient.delete(`/properties/${id}`),
  // Boost system
  getBoostPlans: () => apiClient.get('/properties/boosts/plans'),
  purchaseBoost: (propertyId, data) => apiClient.post(`/properties/${propertyId}/boost`, data),
  // Admin verification
  adminVerify: (propertyId) => apiClient.post(`/properties/${propertyId}/verify`),
  adminUnverify: (propertyId) => apiClient.post(`/properties/${propertyId}/unverify`),
  // Renewal & expiry
  listingPlans: () => apiClient.get('/properties/plans/listing'),
  myExpiring: (days = 3) => apiClient.get('/properties/me/expiring', { params: { days } }),
  myListingQuota: () => apiClient.get('/properties/me/quota'),
  renew: (propertyId, data) => apiClient.post(`/properties/${propertyId}/renew`, data),
  renewBulk: (data) => apiClient.post('/properties/renew-bulk', data),
  renewableCount: () => apiClient.get('/properties/me/renewable-count'),
};

// Admin APIs
export const adminAPI = {
  getAllProperties: () => apiClient.get('/admin/properties'),
  getPendingProperties: () => apiClient.get('/admin/properties/pending'),
  approveProperty: (id) => apiClient.put(`/admin/properties/${id}/approve`),
  rejectProperty: (id, payload) => apiClient.put(`/admin/properties/${id}/reject`, payload || {}),
  bulkModerateProperties: ({ action, ids, reason_code, reason_detail }) =>
    apiClient.post('/admin/properties/bulk-action', { action, ids, reason_code, reason_detail }),
  getAllUsers: () => apiClient.get('/admin/users'),
  deleteUser: (id) => apiClient.delete(`/admin/users/${id}`),
};

// Favorite APIs
export const favoriteAPI = {
  getAll: () => apiClient.get('/favorites'),
  add: (propertyId) => apiClient.post(`/favorites/${propertyId}`),
  remove: (propertyId) => apiClient.delete(`/favorites/${propertyId}`),
};

// Saved Searches — persist a filter+bbox query and receive email alerts
export const savedSearchesAPI = {
  list: () => apiClient.get('/saved-searches'),
  create: (data) => apiClient.post('/saved-searches', data),
  remove: (id) => apiClient.delete(`/saved-searches/${id}`),
  runNow: (id) => apiClient.post(`/saved-searches/${id}/run`),
};

// Marketing APIs
export const marketingAPI = {
  getPlans: () => apiClient.get('/marketing/plans'),
  getBankInfo: () => apiClient.get('/marketing/bank-info'),
  getActiveAds: () => apiClient.get('/marketing/ads/active'),
  createAd: (data) => apiClient.post('/marketing/ads', data),
  payAd: (adId, data) => apiClient.post(`/marketing/ads/${adId}/pay`, data),
  getMyAds: () => apiClient.get('/marketing/ads/mine'),
  updateMyAd: (adId, data) => apiClient.patch(`/marketing/ads/${adId}`, data),
  deleteAd: (adId) => apiClient.delete(`/marketing/ads/${adId}`),
  trackImpression: (adId) => apiClient.post(`/marketing/ads/${adId}/impression`),
  trackClick: (adId) => apiClient.post(`/marketing/ads/${adId}/click`),
  // Public dynamic
  getAdvertiserTypes: () => apiClient.get('/marketing/advertiser-types'),
  // Admin
  adminListAds: (statusFilter) =>
    apiClient.get('/marketing/admin/ads', { params: statusFilter ? { status_filter: statusFilter } : {} }),
  adminApproveAd: (adId) => apiClient.post(`/marketing/admin/ads/${adId}/approve`),
  adminRejectAd: (adId) => apiClient.post(`/marketing/admin/ads/${adId}/reject`),
  // Admin CMS — Plans
  adminListPlans: () => apiClient.get('/admin/marketing/plans'),
  adminCreatePlan: (data) => apiClient.post('/admin/marketing/plans', data),
  adminUpdatePlan: (key, data) => apiClient.put(`/admin/marketing/plans/${key}`, data),
  adminDeletePlan: (key) => apiClient.delete(`/admin/marketing/plans/${key}`),
  // Admin CMS — Advertiser types
  adminListTypes: () => apiClient.get('/admin/marketing/advertiser-types'),
  adminCreateType: (data) => apiClient.post('/admin/marketing/advertiser-types', data),
  adminUpdateType: (key, data) => apiClient.put(`/admin/marketing/advertiser-types/${key}`, data),
  adminDeleteType: (key) => apiClient.delete(`/admin/marketing/advertiser-types/${key}`),
  // Admin analytics
  adminAnalytics: (days = 30) => apiClient.get(`/admin/marketing/analytics?days=${days}`),
};

// Development Leads APIs
export const developmentAPI = {
  submitLead: (data) => apiClient.post('/developments/leads', data),
  // Admin
  adminListLeads: (statusFilter) =>
    apiClient.get('/developments/admin/leads', { params: statusFilter ? { status_filter: statusFilter } : {} }),
  adminUpdateLeadStatus: (id, status) =>
    apiClient.put(`/developments/admin/leads/${id}/status`, { status }),
  adminDeleteLead: (id) => apiClient.delete(`/developments/admin/leads/${id}`),
  // AI Power Pack
  aiScoreLead: (id) => apiClient.post(`/developments/admin/leads/${id}/score`),
  aiDraftReply: (id) => apiClient.post(`/developments/admin/leads/${id}/draft-reply`),
  aiSendReply: (id, data) => apiClient.post(`/developments/admin/leads/${id}/send-reply`, data),
  sendWeeklyReport: () => apiClient.post('/developments/admin/leads/intelligence/send-report'),
};

// Estimation APIs
export const estimationAPI = {
  getPricing: () => apiClient.get('/estimation/pricing'),
  preview: (bien) => apiClient.post('/estimation/preview', bien),
  createOrder: (data) => apiClient.post('/estimation/orders', data),
  payOrder: (id, data) => apiClient.post(`/estimation/orders/${id}/pay`, data),
  getOrder: (id) => apiClient.get(`/estimation/orders/${id}`),
  // Admin
  adminListOrders: (statusFilter) =>
    apiClient.get('/estimation/admin/orders', { params: statusFilter ? { status_filter: statusFilter } : {} }),
  adminDeleteOrder: (id) => apiClient.delete(`/estimation/admin/orders/${id}`),
};

// Business APIs (Agent Pro, Premium Sale, Market Reports)
export const businessAPI = {
  // Agent Pro
  getAgentProPlan: () => apiClient.get('/business/agent-pro/plan'),
  getAgentProPlans: () => apiClient.get('/business/agent-pro/plans'),
  getMyAgentProSubscription: () => apiClient.get('/business/agent-pro/me'),
  subscribeAgentPro: (data) => apiClient.post('/business/agent-pro/subscribe', data),
  getAgentProUpgradeEligibility: () => apiClient.get('/business/agent-pro/upgrade-eligibility'),
  // P5 — Installment (2x) flow
  getMyInstallmentStatus: () => apiClient.get('/business/agent-pro/my-installment-status'),
  payInstallment: (data) => apiClient.post('/business/agent-pro/pay-installment', data),
  // Premium Sale
  getPremiumSaleInfo: () => apiClient.get('/business/premium-sale/info'),
  createMandate: (data) => apiClient.post('/business/premium-sale/mandate', data),
  // Market Reports
  getReportPlans: () => apiClient.get('/business/market-reports/plans'),
  getReportIssues: () => apiClient.get('/business/market-reports/issues'),
  draftReports: (data) => apiClient.post('/business/market-reports/draft', data),
  subscribeReports: (data) => apiClient.post('/business/market-reports/subscribe', data),
  // Admin
  adminListAgentSubs: () => apiClient.get('/business/admin/agent-pro/subscriptions'),
  adminListMandates: (statusFilter) =>
    apiClient.get('/business/admin/premium-sale/mandates', { params: statusFilter ? { status_filter: statusFilter } : {} }),
  adminUpdateMandateStatus: (id, status, finalPrice) =>
    apiClient.put(`/business/admin/premium-sale/mandates/${id}/status`, null, {
      params: { new_status: status, ...(finalPrice ? { final_price: finalPrice } : {}) },
    }),
  adminListReportSubs: () => apiClient.get('/business/admin/market-reports/subscriptions'),
  adminRevenue: (period = 'all') => apiClient.get('/business/admin/revenue', { params: { period } }),
  adminRevenueMonthly: (months = 12) => apiClient.get('/business/admin/revenue/monthly', { params: { months } }),
  adminRevenueExport: (period = 'all', fmt = 'csv') =>
    apiClient.get('/business/admin/revenue/export', { params: { period, fmt }, responseType: 'blob' }),
};

// Agents directory
export const agentsAPI = {
  list: (params = {}) => apiClient.get('/agents', { params }),
  get: (id) => apiClient.get(`/agents/${id}`),
  listReviews: (id) => apiClient.get(`/agents/${id}/reviews`),
  createReview: (id, data) => apiClient.post(`/agents/${id}/reviews`, data),
  replyToReview: (agentId, reviewId, data) =>
    apiClient.post(`/agents/${agentId}/reviews/${reviewId}/reply`, data),
  canReply: (agentId) => apiClient.get(`/agents/me/can-reply/${agentId}`),
  myReceivedReviews: () => apiClient.get('/agents/me/reviews'),
};

// Bank Leads (CPL)
export const bankLeadsAPI = {
  create: (data) => apiClient.post('/bank-leads', data),
  adminList: (params = {}) => apiClient.get('/bank-leads/admin/leads', { params }),
  adminUpdate: (id, data) => apiClient.patch(`/bank-leads/admin/leads/${id}`, data),
  adminStats: (days = 30) => apiClient.get('/bank-leads/admin/stats', { params: { days } }),
  adminInvoices: (month) => apiClient.get('/bank-leads/admin/invoices', { params: { month } }),
  adminInvoicePdf: (bankName, month) =>
    apiClient.get('/bank-leads/admin/invoice', {
      params: { bank_name: bankName, month },
      responseType: 'blob',
    }),
};

// Legal professionals (notaries / lawyers) — affiliation
export const legalProsAPI = {
  list: (params = {}) => apiClient.get('/legal-pros', { params }),
  get: (id) => apiClient.get(`/legal-pros/${id}`),
  refer: (id, data) => apiClient.post(`/legal-pros/${id}/refer`, data),
  adminListAll: () => apiClient.get('/legal-pros/admin/all'),
  adminCreate: (data) => apiClient.post('/legal-pros/admin', data),
  adminDelete: (id) => apiClient.delete(`/legal-pros/admin/${id}`),
  adminListReferrals: (params = {}) => apiClient.get('/legal-pros/admin/referrals', { params }),
  adminUpdateReferral: (id, data) => apiClient.patch(`/legal-pros/admin/referrals/${id}`, data),
  adminStats: () => apiClient.get('/legal-pros/admin/stats'),
  // Reviews
  listReviews: (id) => apiClient.get(`/legal-pros/${id}/reviews`),
  canReview: (id) => apiClient.get(`/legal-pros/${id}/reviews/can-review`),
  createReview: (id, data) => apiClient.post(`/legal-pros/${id}/reviews`, data),
};

// 360° Virtual Tours
export const tour360API = {
  getPricing: () => apiClient.get('/properties/360/pricing'),
  purchase: (propertyId, data) => apiClient.post(`/properties/${propertyId}/360/purchase`, data),
  update: (propertyId, data) => apiClient.put(`/properties/${propertyId}/360`, data),
  disable: (propertyId) => apiClient.delete(`/properties/${propertyId}/360`),
};

// Mobile Money payments (MTN MoMo + Orange Money)
export const mobileMoneyAPI = {
  getProviders: () => apiClient.get('/payments/mobile-money/providers'),
  initiate: (data) => apiClient.post('/payments/mobile-money/initiate', data),
  status: (referenceId) => apiClient.get(`/payments/mobile-money/status/${referenceId}`),
};

// Services Marketplace (surveyors, architects, movers, handymen)
export const servicesAPI = {
  listCategories: () => apiClient.get('/service-providers/categories'),
  list: (params = {}) => apiClient.get('/service-providers', { params }),
  get: (id) => apiClient.get(`/service-providers/${id}`),
  createMission: (id, data) => apiClient.post(`/service-providers/${id}/missions`, data),
  // Public mission tracking by reference (no auth required)
  trackMission: (reference) => apiClient.get(`/service-providers/missions/track/${reference}`),
  listReviews: (id) => apiClient.get(`/service-providers/${id}/reviews`),
  canReview: (id) => apiClient.get(`/service-providers/${id}/reviews/can-review`),
  createReview: (id, data) => apiClient.post(`/service-providers/${id}/reviews`, data),
  subscribe: (id, data) => apiClient.post(`/service-providers/${id}/subscribe`, data),
  // Admin
  adminListAll: () => apiClient.get('/service-providers/admin/all'),
  adminCreate: (data) => apiClient.post('/service-providers/admin', data),
  adminDelete: (id) => apiClient.delete(`/service-providers/admin/${id}`),
  adminListMissions: (params = {}) => apiClient.get('/service-providers/admin/missions', { params }),
  adminUpdateMission: (id, data) => apiClient.patch(`/service-providers/admin/missions/${id}`, data),
  adminStats: () => apiClient.get('/service-providers/admin/stats'),
  // Marketplace IA
  adminKpis: (months = 12) => apiClient.get('/service-providers/admin/kpis', { params: { months } }),
  adminVendorPerformance: (params = {}) => apiClient.get('/service-providers/admin/vendor-performance', { params }),
  adminModerate: (id) => apiClient.post(`/service-providers/admin/${id}/moderate`),
};

// Self-onboarding for legal & service pros
export const becomeProAPI = {
  pricing: () => apiClient.get('/become-pro/pricing'),
  draft: (data) => apiClient.post('/become-pro/draft', data),
  apply: (data) => apiClient.post('/become-pro/apply', data),
  status: (ref) => apiClient.get(`/become-pro/status/${ref}`),
  adminPending: () => apiClient.get('/become-pro/admin/pending'),
  adminVerify: (data) => apiClient.post('/become-pro/admin/verify', data),
  adminConfirmPayment: (data) => apiClient.post('/become-pro/admin/confirm-payment', data),
  adminReject: (data) => apiClient.post('/become-pro/admin/reject', data),
};

// Pro Portal (private dashboard for partner pros)
const proHttp = axios.create({ baseURL: API_URL, withCredentials: true });
attachCsrf(proHttp);
export const proPortalAPI = {
  // Auth (OTP SMS via phone — preferred) + legacy magic-link
  requestOtp: (phone) => apiClient.post('/pro-portal/request-otp', { phone }),
  verifyOtp: (phone, code) => apiClient.post('/pro-portal/verify-otp', { phone, code }),
  mockOtpPeek: (phone) => apiClient.get(`/pro-portal/mock-otp-peek?phone=${encodeURIComponent(phone)}`),
  requestLink: (email) => apiClient.post('/pro-portal/request-link', { email }),
  verifyToken: (token) => apiClient.post('/pro-portal/verify-token', { token }),
  // Dashboard
  logout: () => proHttp.post('/pro-portal/logout'),
  me: () => proHttp.get('/pro-portal/me'),
  stats: () => proHttp.get('/pro-portal/stats'),
  updateProfile: (data) => proHttp.patch('/pro-portal/profile', data),
  renew: (data) => proHttp.post('/pro-portal/renew', data),
  renewStatus: (ref) => proHttp.get(`/pro-portal/renew/status/${ref}`),
  // Services CRUD
  listServices: () => proHttp.get('/pro-portal/services'),
  createService: (data) => proHttp.post('/pro-portal/services', data),
  updateService: (id, data) => proHttp.patch(`/pro-portal/services/${id}`, data),
  deleteService: (id) => proHttp.delete(`/pro-portal/services/${id}`),
  // Featured Card monetization
  featuredDraft: (package_id) => proHttp.post('/pro-portal/featured/draft', { package_id }),
  // Leads
  listLeads: () => proHttp.get('/pro-portal/leads'),
  leadUnlockDraft: (id) => proHttp.post(`/pro-portal/leads/${id}/unlock-draft`),
  // AI Tools
  aiDescribe: (data) => proHttp.post('/pro-portal/ai/describe-service', data),
  aiPricing: (data) => proHttp.post('/pro-portal/ai/suggest-pricing', data),
  aiQuote: (data) => proHttp.post('/pro-portal/ai/generate-quote', data),
  aiReply: (data) => proHttp.post('/pro-portal/ai/suggest-reply', data),
};

// Financial Partners (carrousel propriétés)
export const partnerApplicationsAPI = {
  submit: (payload) => apiClient.post('/partner-applications', payload),
  adminList: () => apiClient.get('/admin/partner-applications'),
  adminUpdate: (id, payload) => apiClient.patch(`/admin/partner-applications/${id}`, payload),
  reminder: () => apiClient.get('/admin/partner-applications/reminder'),
};

export const financialPartnersAPI = {
  // Public
  listPublic: () => apiClient.get('/financial-partners'),
  trackImpression: (id) => apiClient.post(`/financial-partners/${id}/track-impression`),
  trackClick: (id) => apiClient.post(`/financial-partners/${id}/track-click`),
  getSponsorTiers: () => apiClient.get('/financial-partners/sponsor-tiers'),
  initiateSponsoring: (data) => apiClient.post('/financial-partners/sponsor/initiate', data),
  sponsorStatus: (id) => apiClient.get(`/financial-partners/sponsor/status/${id}`),
  // Admin
  adminList: () => apiClient.get('/admin/financial-partners'),
  adminCreate: (data) => apiClient.post('/admin/financial-partners', data),
  adminUpdate: (id, data) => apiClient.put(`/admin/financial-partners/${id}`, data),
  adminDelete: (id) => apiClient.delete(`/admin/financial-partners/${id}`),
  adminAnalytics: (id, days = 30) => apiClient.get(`/admin/financial-partners/${id}/analytics`, { params: { days } }),
  adminAnalyticsSummary: () => apiClient.get('/admin/financial-partners/analytics/summary'),
  adminSendMonthlyReports: (force = true) => apiClient.post(`/admin/financial-partners/send-monthly-reports?force=${force}`),
  adminExpirations: (days = 14) => apiClient.get('/admin/financial-partners/expirations', { params: { days } }),
  adminRunExpirationAlerts: (force = false) => apiClient.post(`/admin/financial-partners/run-expiration-alerts?force=${force}`),
};

// Centralized Pricing config
export const pricingAPI = {
  getPublic: () => apiClient.get('/pricing'),
  adminGetCatalog: () => apiClient.get('/admin/pricing'),
  adminSet: (key, value) => apiClient.put(`/admin/pricing/${key}`, { value: parseInt(value, 10) }),
  adminReset: (key) => apiClient.delete(`/admin/pricing/${key}`),
};

// Payment Packages CMS (admin)
export const packagesAPI = {
  publicList: (params) => apiClient.get('/packages', { params }),
  adminList: () => apiClient.get('/admin/packages'),
  adminGet: (key) => apiClient.get(`/admin/packages/${key}`),
  adminCreate: (data) => apiClient.post('/admin/packages', data),
  adminUpdate: (key, data) => apiClient.put(`/admin/packages/${key}`, data),
  adminDelete: (key) => apiClient.delete(`/admin/packages/${key}`),
  adminAudit: (key) => apiClient.get(`/admin/packages/${key}/audit`),
  adminRollback: (key, auditId) => apiClient.post(`/admin/packages/${key}/rollback/${auditId}`),
  adminRevenueByCategory: (days = 30) => apiClient.get('/admin/packages/revenue-by-category', { params: { days } }),
};

// CMS — Editorial content
export const cmsAPI = {
  getPublic: () => apiClient.get('/cms'),
  adminGetCatalog: () => apiClient.get('/admin/cms'),
  adminSet: (key, value) => apiClient.put(`/admin/cms/${encodeURIComponent(key)}`, { value }),
  adminReset: (key) => apiClient.delete(`/admin/cms/${encodeURIComponent(key)}`),
  adminGetHistory: (key) => apiClient.get(`/admin/cms-history/${encodeURIComponent(key)}`),
  adminRollback: (historyId) => apiClient.post(`/admin/cms-history/${historyId}/rollback`),
};

// Articles & Blog
export const articlesAPI = {
  listCategories: () => apiClient.get('/articles/categories'),
  listPublic: (params) => apiClient.get('/articles', { params }),
  getBySlug: (slug) => apiClient.get(`/articles/${slug}`),
  getTrendingWeekly: () => apiClient.get('/articles/trending/weekly'),
  adminList: (params) => apiClient.get('/admin/articles', { params }),
  adminGetOne: (id) => apiClient.get(`/admin/articles/${id}`),
  adminCreate: (data) => apiClient.post('/admin/articles', data),
  adminUpdate: (id, data) => apiClient.put(`/admin/articles/${id}`, data),
  adminDelete: (id) => apiClient.delete(`/admin/articles/${id}`),
  trackImpression: (id) => apiClient.post(`/articles/${id}/cta-impression`),
  trackClick: (id) => apiClient.post(`/articles/${id}/cta-click`),
  adminCtaAnalytics: (id, days = 30) => apiClient.get(`/admin/articles/${id}/cta-analytics`, { params: { days } }),
};

// AI — Power Pack (Gemini + Claude + Nano Banana)
export const aiAPI = {
  // U2 — photo(s) → annonce (titre + description + tags + features)
  describeProperty: (imagesBase64, hints) =>
    apiClient.post('/ai/property/describe', { images_base64: imagesBase64, hints }),
  // U3 — analyse marché à partir d'une estimation calculée
  estimationMarket: (bien, estimation) =>
    apiClient.post('/ai/estimation/market', { bien, estimation }),
  // A1 — modération IA d'une annonce
  moderateProperty: (propertyId) =>
    apiClient.post(`/admin/ai/moderate/${propertyId}`),
  // A5 v2 — preview moderation pour le formulaire de création (avant submit)
  previewModerateImages: (images) =>
    apiClient.post('/ai/property/preview-moderate', { images }),
  // A2 — génération d'article de blog
  generateArticle: (payload) =>
    apiClient.post('/admin/ai/articles/generate', payload),
};

// Admin Properties Pro (analytics, intelligence, feed)
export const adminPropertiesProAPI = {
  analytics: (days = 30) => apiClient.get('/admin/properties/analytics', { params: { days } }),
  intelligence: (useAi = true) => apiClient.get('/admin/properties/intelligence', { params: { use_ai_commentary: useAi } }),
  feed: (params = {}) => apiClient.get('/admin/properties/feed', { params }),
};

// Admin Users Pro
export const adminUsersProAPI = {
  analytics: (days = 30) => apiClient.get('/admin/users/analytics', { params: { days } }),
  intelligence: (useAi = true) => apiClient.get('/admin/users/intelligence', { params: { use_ai_commentary: useAi } }),
  feed: (params = {}) => apiClient.get('/admin/users/feed', { params }),
};

// Admin Reviews Pro
export const adminReviewsProAPI = {
  analytics: (days = 30) => apiClient.get('/admin/reviews/analytics', { params: { days } }),
  intelligence: (useAi = true) => apiClient.get('/admin/reviews/intelligence', { params: { use_ai_commentary: useAi } }),
  feed: (params = {}) => apiClient.get('/admin/reviews/feed', { params }),
};

// Admin Estimations Pro
export const adminEstimationsProAPI = {
  analytics: (days = 30) => apiClient.get('/admin/estimations/analytics', { params: { days } }),
  intelligence: (useAi = true) => apiClient.get('/admin/estimations/intelligence', { params: { use_ai_commentary: useAi } }),
  feed: (params = {}) => apiClient.get('/admin/estimations/feed', { params }),
};

// Onboarding pack (Nouveau Propriétaire / Nouveau Locataire)
export const onboardingAPI = {
  getPack: (params = {}) => apiClient.get('/onboarding/pack', { params }),
  submitLead: (data) => apiClient.post('/onboarding/lead', data),
  adminListPartners: (params = {}) => apiClient.get('/admin/onboarding/partners', { params }),
  adminCreatePartner: (data) => apiClient.post('/admin/onboarding/partners', data),
  adminUpdatePartner: (id, data) => apiClient.put(`/admin/onboarding/partners/${id}`, data),
  adminDeletePartner: (id) => apiClient.delete(`/admin/onboarding/partners/${id}`),
  adminListLeads: (params = {}) => apiClient.get('/admin/onboarding/leads', { params }),
  adminAnalytics: (days = 30) => apiClient.get('/admin/onboarding/analytics', { params: { days } }),
};

// RTB bank bidding
export const rtbAPI = {
  getSettings: () => apiClient.get('/admin/rtb/settings'),
  updateSettings: (data) => apiClient.put('/admin/rtb/settings', data),
  listRules: () => apiClient.get('/admin/rtb/rules'),
  createRule: (data) => apiClient.post('/admin/rtb/rules', data),
  updateRule: (id, data) => apiClient.put(`/admin/rtb/rules/${id}`, data),
  deleteRule: (id) => apiClient.delete(`/admin/rtb/rules/${id}`),
  listAuctions: (limit = 50) => apiClient.get('/admin/rtb/auctions', { params: { limit } }),
  analytics: (days = 30) => apiClient.get('/admin/rtb/analytics', { params: { days } }),
  pnl: (days = 30) => apiClient.get('/admin/rtb/pnl', { params: { days } }),
  lostAuctions: (days = 30, maxGap = 2000) => apiClient.get('/admin/rtb/lost-auctions', { params: { days, max_gap_fcfa: maxGap } }),
  coachRule: (ruleId) => apiClient.post(`/admin/rtb/coach/${ruleId}`),
};

// Admin Reports — universal PDF generator + scheduling
export const reportsAPI = {
  listTypes: () => apiClient.get('/admin/reports/types'),
  generate: (data) =>
    apiClient.post('/admin/reports/generate', data, { responseType: 'blob' }),
  listScheduled: () => apiClient.get('/admin/reports/scheduled'),
  createSchedule: (data) => apiClient.post('/admin/reports/scheduled', data),
  updateSchedule: (id, data) => apiClient.put(`/admin/reports/scheduled/${id}`, data),
  deleteSchedule: (id) => apiClient.delete(`/admin/reports/scheduled/${id}`),
  runNow: (id) => apiClient.post(`/admin/reports/scheduled/${id}/run-now`),
};

// i18n — on-demand translation for property listings (cached server-side)
export const i18nAPI = {
  translateProperty: (data) => apiClient.post('/i18n/translate-property', data),
};

export const adminTaxonomyAPI = {
  listLabels: () => apiClient.get('/admin/taxonomy/labels'),
  setLabel: (value, payload) => apiClient.put(`/admin/taxonomy/labels/${value}`, payload),
  clearLabel: (value) => apiClient.delete(`/admin/taxonomy/labels/${value}`),
  refresh: () => apiClient.post('/admin/taxonomy/refresh'),
  version: () => apiClient.get('/admin/taxonomy/version'),
};

export const legalAPI = {
  publicGet: (slug) => apiClient.get(`/legal/${slug}`),
  adminList: () => apiClient.get('/admin/legal'),
  adminGet: (slug) => apiClient.get(`/admin/legal/${slug}`),
  adminSave: (slug, payload) => apiClient.put(`/admin/legal/${slug}`, payload),
};

export const adminAccountsAPI = {
  me: () => apiClient.get('/admin/accounts/me'),
  catalog: () => apiClient.get('/admin/accounts/catalog'),
  list: () => apiClient.get('/admin/accounts'),
  invite: (payload) => apiClient.post('/admin/accounts/invite', payload),
  cancelInvite: (id) => apiClient.delete(`/admin/accounts/invite/${id}`),
  update: (userId, payload) => apiClient.patch(`/admin/accounts/${userId}`, payload),
  demote: (userId) => apiClient.delete(`/admin/accounts/${userId}`),
  audit: (limit = 100) => apiClient.get(`/admin/accounts/audit?limit=${limit}`),
};

// Public invite endpoints (no auth)
export const adminInvitePublicAPI = {
  describe: (token) => axios.get(`${API_URL}/admin-invite/${token}`),
  accept: (token, payload) => axios.post(`${API_URL}/admin-invite/${token}/accept`, payload),
};

// Navbar promotions — featured visuals in the mega-menus.
// Public endpoint is unauth + cached (60 s); admin endpoints require
// `marketing:write`.
export const navPromotionsAPI = {
  // Public — no auth needed. We use plain axios to skip the auth interceptor.
  getPublic: () => axios.get(`${API_URL}/nav-promotions`),
  adminList: () => apiClient.get('/admin/nav-promotions'),
  adminUpsert: (slot, payload) => apiClient.put(`/admin/nav-promotions/${slot}`, payload),
  adminReset: (slot) => apiClient.delete(`/admin/nav-promotions/${slot}`),
};

// Boost packs — combined boost offers (e.g. "Pack Meublé Premium"). The pack
// config (price, components, active flag) is fully admin-editable.
export const boostPacksAPI = {
  getPublic: (packId) => axios.get(`${API_URL}/boost-packs/${packId}`),
  adminGet: (packId) => apiClient.get(`/admin/boost-packs/${packId}`),
  adminUpsert: (packId, payload) => apiClient.put(`/admin/boost-packs/${packId}`, payload),
  adminReset: (packId) => apiClient.delete(`/admin/boost-packs/${packId}`),
  purchase: (propertyId, packId, payload) =>
    apiClient.post(`/properties/${propertyId}/boost-pack/${packId}`, payload),
};

// Home hero carousel slides — admin-curated city showcases. Public endpoint
// also merges boosted (homeFeaturedUntil) properties at the top.
export const homeHeroSlidesAPI = {
  getPublic: () => axios.get(`${API_URL}/home/hero-slides`),
  adminList: () => apiClient.get('/admin/home/hero-slides'),
  adminCreate: (payload) => apiClient.post('/admin/home/hero-slides', payload),
  adminUpdate: (slideId, payload) => apiClient.put(`/admin/home/hero-slides/${slideId}`, payload),
  adminDelete: (slideId) => apiClient.delete(`/admin/home/hero-slides/${slideId}`),
  adminReorder: (ids) => apiClient.post('/admin/home/hero-slides/reorder', { ids }),
  adminSeedDefaults: () => apiClient.post('/admin/home/hero-slides/seed-defaults'),
};

// Home TikTok carousel — 60s flash property tours. Admin curates the base
// list; agents can pay 8 000 FCFA / 7 j to surface their own video at the top.
export const homeTikTokAPI = {
  getPublic: () => axios.get(`${API_URL}/home/tiktok-videos`),
  createBoostDraft: (payload) => apiClient.post('/home/tiktok-videos/boost-draft', payload),
  adminList: () => apiClient.get('/admin/home/tiktok-videos'),
  adminCreate: (payload) => apiClient.post('/admin/home/tiktok-videos', payload),
  adminUpdate: (videoId, payload) => apiClient.put(`/admin/home/tiktok-videos/${videoId}`, payload),
  adminDelete: (videoId) => apiClient.delete(`/admin/home/tiktok-videos/${videoId}`),
  adminReorder: (ids) => apiClient.post('/admin/home/tiktok-videos/reorder', { ids }),
};

export const tutorialsAPI = {
  list: () => axios.get(`${API_URL}/tutorials`),
  videoUrl: (src) => `${process.env.REACT_APP_BACKEND_URL}${src}`,
  adminList: () => apiClient.get('/admin/tutorials'),
  adminGetSettings: () => apiClient.get('/admin/tutorials/settings'),
  adminSetSettings: (enabled) => apiClient.put('/admin/tutorials/settings', { enabled }),
  adminCreate: (payload) => apiClient.post('/admin/tutorials', payload),
  adminUpdate: (id, payload) => apiClient.put(`/admin/tutorials/${id}`, payload),
  adminDelete: (id) => apiClient.delete(`/admin/tutorials/${id}`),
  adminUploadChunk: (id, form) => apiClient.post(`/admin/tutorials/${id}/upload-chunk`, form),
  track: (id, payload) => axios.post(`${API_URL}/tutorials/${id}/event`, payload),
};

// ── Data API B2B — public + user-facing + admin ─────────────────────
export const dataApiPublic = {
  getPlans: () => apiClient.get('/data-api/plans'),
};

export const dataApiUser = {
  getSubscription: () => apiClient.get('/data-api/subscription'),
  getUsage: (days = 30) => apiClient.get('/data-api/usage', { params: { days } }),
  listKeys: () => apiClient.get('/data-api/keys'),
  createKey: (name) => apiClient.post('/data-api/keys', { name }),
  revokeKey: (id) => apiClient.delete(`/data-api/keys/${id}`),
  subscribe: (plan_code, months = 1) => apiClient.post('/data-api/subscribe', { plan_code, months }),
  confirmSubscribe: (plan_code, months, payment_reference) =>
    apiClient.post('/data-api/subscribe/confirm', { plan_code, months, payment_reference }),
};

export const adminDataApi = {
  listPlans: () => apiClient.get('/admin/api-plans'),
  updatePlan: (code, payload) => apiClient.put(`/admin/api-plans/${code}`, payload),
  createPlan: (payload) => apiClient.post('/admin/api-plans', payload),
  listSubscriptions: (params = {}) => apiClient.get('/admin/api-subscriptions', { params }),
  moderateSubscription: (id, action) => apiClient.post(`/admin/api-subscriptions/${id}/${action}`),
  manualActivate: (payload) => apiClient.post('/admin/api-subscriptions/manual-activate', payload),
  analytics: () => apiClient.get('/admin/api-analytics'),
  mrrByPlan: () => apiClient.get('/admin/api-analytics/mrr-by-plan'),
  listEmails: (limit = 50) => apiClient.get('/admin/api-emails', { params: { limit } }),
};

export default apiClient;
